import React, { useState, useEffect, useRef, useCallback } from "react";

import { Box, Tabs, Tab, Grid } from "@mui/material";
import AlertsTab from "./TabsSections/AlertsTab";
import NotesTab from "./TabsSections/NotesTab";
import api from "../../api/axios";
import MessagesTab from "./TabsSections/MessagesTab";
import CaseRichTextEditor from "../../Components/Common/Editor/CaseRichTextEditor";

import dayjs from "dayjs";

const TabsSection = ({ caseId, lawyerData, dataCase, glassSurface }) => {
  const Swal = require("sweetalert2");

  // ===============================
  // STATE VARIABLES
  // ===============================
  const [dataCaseNotesAlertsMessages, setDataCaseNotesAlertsMessages] =
    useState([]);

  const DEFAULT_TAB = 1;
  const [tabValue, setTabValue] = useState(DEFAULT_TAB);

  const [addDataTab, setAddDataTab] = useState(false);
  const [editDataTab, setEditDataTab] = useState(false);

  const SAFE_TABS = [0, 1, 2];

  const safeSetTabValue = (value) => {
    const normalized = Number(value);
    if (Number.isInteger(normalized) && SAFE_TABS.includes(normalized)) {
      setTabValue(normalized);
    } else {
      setTabValue(DEFAULT_TAB);
    }
  };

  ////

  const [editorState, setEditorState] = useState("");
  const [noteId, setNoteId] = useState(null);
  const [messageId, setMessageId] = useState(null);
  const [alertId, setAlertId] = useState(null);

  const [tabsDataChanged, setTabsDataChanged] = useState(false);
  const [tabsListReloading, setTabsListReloading] = useState(false);
  const [isSavingTabEntry, setIsSavingTabEntry] = useState(false);
  const [isTypingTabEntry, setIsTypingTabEntry] = useState(false);

  // Estados de selección
  const [selectionStyleAlert, setSelectionStyleAlert] = useState(null);
  const [selectionStyleNote, setSelectionStyleNote] = useState(null);
  const [selectionStyleMessage, setSelectionStyleMessage] = useState(null);

  // Control para detectar cambios
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const [dateConfig, setDateConfig] = React.useState(dayjs());
  const latestEditorStateRef = useRef(editorState);
  const typingTimeoutRef = useRef(null);

  // ===============================
  // FETCH NOTES/ALERTS
  // ===============================
  const getDataAlertsNotesMessages = useCallback(async () => {
    const url = `${process.env.REACT_APP_API_URL}/cases/getalertsandnotes`;
    const formData = new FormData();
    formData.append("id", caseId);

    try {
      const notesAlerts = await api.post(url, formData);
      const { alerts, notes, messages } = notesAlerts?.data?.notes_alerts || {};
      setDataCaseNotesAlertsMessages({ alerts, notes, messages });
    } catch (error) {
      console.error("Error fetching notes/alerts data:", error);
    } finally {
      setTabsListReloading(false);
    }
  }, [caseId]);

  useEffect(() => {
    getDataAlertsNotesMessages();
  }, [tabsDataChanged, getDataAlertsNotesMessages]);

  // ===============================
  // EDITOR HANDLERS
  // ===============================
  const clearEditor = () => {
    const emptyEditor = "";
    latestEditorStateRef.current = emptyEditor;
    setEditorState(emptyEditor);
    setUnsavedChanges(false);
  };

  const onEditorChange = (newState) => {
    latestEditorStateRef.current = newState;
    setEditorState(newState);
    setUnsavedChanges(true);
    setIsTypingTabEntry(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(
      () => setIsTypingTabEntry(false),
      600,
    );
  };

  const normalizeRichTextForStorage = (html = "") => {
    if (!html) return "";

    const controlCharsRegex = new RegExp(
      "[\\u0000-\\u0008\\u000B\\u000C\\u000E-\\u001F\\u007F]",
      "g",
    );

    return html
      .normalize("NFC")
      .replace(controlCharsRegex, "")
      .replace(/[\u{10000}-\u{10FFFF}]/gu, (char) => {
        const codePoint = char.codePointAt(0);
        return codePoint ? `&#${codePoint};` : "";
      });
  };

  const hasMeaningfulContent = (html = "") => {
    const plain = html
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim();

    return plain.length > 0;
  };

  const getApiErrorMessage = (error, fallback = "No se pudo guardar") => {
    const responseData = error?.response?.data;

    return (
      responseData?.message || responseData?.error || error?.message || fallback
    );
  };

  const validateTabsSaveResponse = (responseData, rute) => {
    if (!responseData) {
      throw new Error("No se recibió respuesta del servidor.");
    }

    if (responseData.error || responseData.success === false) {
      throw new Error(
        responseData.message || "No se pudo guardar el registro.",
      );
    }

    const saveMap = {
      notes: ["note_created", "note_updated", "note"],
      alerts: ["alert_created", "alert_updated", "alert"],
    };

    const expectedKeys = saveMap[rute] || [];
    const hasExpectedKey = expectedKeys.some((key) => !!responseData[key]);

    if (!hasExpectedKey) {
      throw new Error(
        responseData.message ||
          "El servidor no confirmó que el registro se guardara.",
      );
    }
  };

  const saveTabEntry = async ({
    url,
    formData,
    rute,
    successTitle,
    fallbackError,
  }) => {
    Swal.fire({
      title: "Guardando...",
      text: "Por favor espera",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const response = await api.post(url, formData);
      validateTabsSaveResponse(response?.data, rute);

      Swal.close();

      await Swal.fire({
        icon: "success",
        title: successTitle,
        timer: 1500,
        showConfirmButton: false,
      });
      return true;
    } catch (error) {
      Swal.close();

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: getApiErrorMessage(error, fallbackError),
      });

      return false;
    }
  };

  const handleCancelDataTabs = () => {
    if (unsavedChanges) {
      Swal.fire({
        title: "¿Descartar cambios?",
        text: "Hay cambios sin guardar. ¿Seguro que deseas cancelar?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#1ab394",
        cancelButtonColor: "#d33",
        reverseButtons: true,
        confirmButtonText: "Sí, descartar",
        cancelButtonText: "Volver",
      }).then((result) => {
        if (result.isConfirmed) {
          clearEditor();
          setAddDataTab(false);
          setEditDataTab(false);
          setNoteId(null);
          setAlertId(null);
          setMessageId(null);
          // setOpenModal(false);
        }
      });
    } else {
      clearEditor();
      setAddDataTab(false);
      setEditDataTab(false);
      setNoteId(null);
      setAlertId(null);
      setMessageId(null);
      // setOpenModal(false);
    }
  };

  // ===============================
  // SUBMIT HANDLER
  // ===============================
  const handleSubmitDataTabs = async (rute, dataTypeLabel, date = "") => {
    if (isSavingTabEntry) return;

    const editorStateToSave = latestEditorStateRef.current || editorState;
    const rawHtmlContent = String(editorStateToSave || "");
    const htmlContent = normalizeRichTextForStorage(rawHtmlContent);

    if (!hasMeaningfulContent(htmlContent)) {
      Swal.fire("Error", "El contenido está vacío", "warning");
      return;
    }

    if (!lawyerData?.id || !dataCase?.id) {
      Swal.fire(
        "Aviso",
        "El expediente debe tener un abogado asignado para trabajar en él.",
        "warning",
      );
      return;
    }

    const formData = new FormData();

    // CREAR
    if (!editDataTab) {
      formData.append("message", htmlContent);
      formData.append("user_id", lawyerData.id);
      formData.append("case_id", dataCase.id);
      formData.append("date", date?.date);

      const confirmResult = await Swal.fire({
        title: `¿Desea guardar la ${dataTypeLabel}?`,
        icon: "warning",
        showDenyButton: true,
        confirmButtonText: "Sí",
        denyButtonText: "No",
        confirmButtonColor: "#1ab394",
        cancelButtonColor: "#d33",
        reverseButtons: true,
      });

      if (!confirmResult?.isConfirmed) return;

      setIsSavingTabEntry(true);
      const saved = await saveTabEntry({
        url: `${process.env.REACT_APP_API_URL}/${rute}/store`,
        formData,
        rute,
        successTitle: "Guardado exitosamente",
        fallbackError: `No se pudo guardar la ${dataTypeLabel.toLowerCase()}`,
      });
      setIsSavingTabEntry(false);

      if (saved) {
        setTabsListReloading(true);
        setTabsDataChanged((prev) => !prev);
        clearEditor();
        setAddDataTab(false);
        setEditDataTab(false);
      }
    }

    // EDITAR
    else {
      const id = rute === "alerts" ? alertId : noteId ? noteId : messageId;
      formData.append("id", id);
      formData.append("message", htmlContent);

      const confirmResult = await Swal.fire({
        title: `¿Desea editar la ${dataTypeLabel}?`,
        icon: "warning",
        showDenyButton: true,
        confirmButtonText: "Sí",
        denyButtonText: "No",
        confirmButtonColor: "#1ab394",
        cancelButtonColor: "#d33",
        reverseButtons: true,
      });

      if (!confirmResult?.isConfirmed) return;

      setIsSavingTabEntry(true);
      const saved = await saveTabEntry({
        url: `${process.env.REACT_APP_API_URL}/${rute}/update`,
        formData,
        rute,
        successTitle: "Actualizado exitosamente",
        fallbackError: `No se pudo actualizar la ${dataTypeLabel.toLowerCase()}`,
      });
      setIsSavingTabEntry(false);

      if (saved) {
        setTabsListReloading(true);
        setTabsDataChanged((prev) => !prev);
        clearEditor();
        setAddDataTab(false);
        setEditDataTab(false);
      }
    }
  };

  const handleSaveShortcut = async () => {
    if (!addDataTab && !editDataTab) return;
    if (tabValue === 0) {
      await handleSubmitDataTabs("alerts", "Alerta", {
        date: dateConfig
          ? dayjs(dateConfig).format("YYYY-MM-DD HH:mm:ss")
          : null,
      });
      return;
    }
    if (tabValue === 1) {
      await handleSubmitDataTabs("notes", "Nota");
    }
  };

  const renderTabLabel = (label, value) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.9 }}>
      <span>{label}</span>
      {unsavedChanges && tabValue === value && (addDataTab || editDataTab) && (
        <Box
          component="span"
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: "#f59e0b",
            boxShadow: "0 0 0 2px rgba(245, 158, 11, 0.2)",
          }}
        />
      )}
    </Box>
  );

  // ===============================
  // DELETE HANDLER
  // ===============================
  const handleDeleteDataTabs = async (id, message, dataType, title) => {
    const result = await Swal.fire({
      title: `¿Eliminar ${title}?`,
      html: `<p style="margin:0">${message}</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Eliminando...",
      text: "Por favor espere",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const url = `${process.env.REACT_APP_API_URL}/${dataType}/destroy`;
      const formData = new FormData();
      formData.append("id", id);

      await api.post(url, formData);

      setTabsListReloading(true);
      setTabsDataChanged((prev) => !prev);

      Swal.fire({
        title: "Eliminado",
        text: `${title} eliminad@ correctamente.`,
        icon: "success",
        confirmButtonColor: "#1ab394",
      });
    } catch (error) {
      console.error(error);
      setTabsListReloading(false);

      Swal.fire({
        title: "Error",
        text: "No se pudo eliminar el registro. Intente nuevamente.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    }
  };

  // ===============================
  // RENDER
  // ===============================
  return (
    <Grid
      container
      item
      xs={12}
      md={12}
      m={"auto"}
      sx={{
        ...glassSurface,
        borderBottomLeftRadius: "10px",
        borderBottomRightRadius: "10px",
        p: { xs: "10px", md: "10px" },
      }}>
      <Box
        sx={{
          width: "100%",
        }}>
        {SAFE_TABS.includes(tabValue) && (
          <Tabs
            sx={{
              background: "rgba(255, 255, 255, 0.58)",
              border: "1px solid rgba(255, 255, 255, 0.52)",
              mb: 1.5,
              "& .MuiTabs-indicator": {
                height: 3,
                backgroundColor: "#1976d2",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 400,
                minHeight: 44,
                color: "#5b6471",
              },
              "& .Mui-selected": {
                color: "#1976d2 !important",
                fontWeight: 400,
              },
            }}
            value={typeof tabValue === "number" ? tabValue : DEFAULT_TAB}
            onChange={(e, newVal) => safeSetTabValue(newVal)}>
            <Tab label={renderTabLabel("Alertas", 0)} value={0} />
            <Tab label={renderTabLabel("Notas", 1)} value={1} />
            <Tab label={renderTabLabel("Mensajes", 2)} value={2} />
          </Tabs>
        )}
        {tabValue === 0 && (
          <AlertsTab
            handleDeleteDataTabs={handleDeleteDataTabs}
            addDataTab={addDataTab}
            setAddDataTab={setAddDataTab}
            editDataTab={editDataTab}
            setEditDataTab={setEditDataTab}
            Editor={CaseRichTextEditor}
            editorState={editorState}
            onEditorChange={onEditorChange}
            handleCancelDataTabs={handleCancelDataTabs}
            handleSubmitDataTabs={handleSubmitDataTabs}
            onSaveShortcut={handleSaveShortcut}
            dataCaseNotesAlertsMessages={dataCaseNotesAlertsMessages}
            setSelectionStyleAlert={setSelectionStyleAlert}
            selectionStyleAlert={selectionStyleAlert}
            dateConfig={dateConfig}
            setDateConfig={setDateConfig}
            listReloading={tabsListReloading}
            isSaving={isSavingTabEntry}
            isTyping={isTypingTabEntry}
          />
        )}
        {tabValue === 1 && (
          <NotesTab
            addDataTab={addDataTab}
            editDataTab={editDataTab}
            setAddDataTab={setAddDataTab}
            setEditDataTab={setEditDataTab}
            Editor={CaseRichTextEditor}
            editorState={editorState}
            onEditorChange={onEditorChange}
            onSaveShortcut={handleSaveShortcut}
            dataCaseNotesAlertsMessages={dataCaseNotesAlertsMessages}
            setSelectionStyleNote={setSelectionStyleNote}
            selectionStyleNote={selectionStyleNote}
            handleDeleteDataTabs={handleDeleteDataTabs}
            handleCancelDataTabs={handleCancelDataTabs}
            handleSubmitDataTabs={handleSubmitDataTabs}
            listReloading={tabsListReloading}
            isSaving={isSavingTabEntry}
            isTyping={isTypingTabEntry}
          />
        )}
        {tabValue === 2 && (
          <MessagesTab
            dataCase={dataCase}
            addDataTab={addDataTab}
            editDataTab={editDataTab}
            setAddDataTab={setAddDataTab}
            setEditDataTab={setEditDataTab}
            Editor={CaseRichTextEditor}
            editorState={editorState}
            onEditorChange={onEditorChange}
            dataCaseNotesAlertsMessages={dataCaseNotesAlertsMessages}
            setSelectionStyleMessage={setSelectionStyleMessage}
            selectionStyleMessage={selectionStyleMessage}
            handleDeleteDataTabs={handleDeleteDataTabs}
            handleCancelDataTabs={handleCancelDataTabs}
            handleSubmitDataTabs={handleSubmitDataTabs}
          />
        )}
        {/* </DialogContent> */}
        {/* <DialogActions>
          <Button onClick={handleCloseModal} color="error">
            Cancelar
          </Button>
          <Button
            onClick={() =>
              handleSubmitDataTabs(tabValue === 0 ? "alerts" : "notes")
            }
            color="primary">
            Guardar
          </Button>
        </DialogActions> */}
        {/* </Dialog> */}
      </Box>
    </Grid>
  );
};

export default TabsSection;
